/**
 * HTTP module.
 * @module src/string
 */
import { isBlank } from './any';
const wordMatcher = /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g;
const firstCharMatcher = /^./g;
/**
 * Returns the string with its first character in upper case mode
 *
 * @function capitalize
 *
 * @param {string} string - string to be converted to capitalize
 * @return {string} the converted string
 *
 * @example
 * capitalize(undefined);
 * // returns undefined
 *
 * @example
 * capitalize('it works');
 * // returns 'It works'
 *
 * @example
 * capitalize('9genius');
 * // returns '9genius'
 *
 */

export const capitalize = string => isBlank(string) ? string : string.toLowerCase().replace(firstCharMatcher, firstChar => firstChar.toUpperCase());
/**
 * Returns a snake case version of a string.
 *
 * @function toSnakeCase
 *
 * @param {string} string - string to be converted to snak case
 * @return {string} the converted string
 *
 * @example
 * toSnakeCase(undefined);
 * // returns undefined
 *
 * @example
 * toSnakeCase('snakeCase');
 * // returns 'snake_case'
 *
 * @example
 * toSnakeCase('CustomerGPS');
 * // returns 'customer_gps'
 *
 * @example
 * toSnakeCase('IP-address');
 * // returns 'ip_address'
 *
 * @example
 * toSnakeCase('Another & Another, one too');
 * // returns 'another_another_one_too'
 *
 * @example
 * toSnakeCase('random ----- Thing123');
 * // returns 'random_thing123'
 *
 * @example
 * toSnakeCase('456Thing123');
 * // returns '456_thing123'
 *
 * @example
 * toSnakeCase('\n\n');
 * // returns '\n\n'
 *
 * @example
 * toSnakeCase(' my Test ');
 * // returns 'my_test'
 *
 */

export const toSnakeCase = string => isBlank(string) ? string : string.match(wordMatcher).map(word => word.toLowerCase()).join('_');
/**
 * Returns a camel case version of a string.
 *
 * @function toCamelCase
 *
 * @param {string} string - string to be converted to camel case
 * @return {string} the converted string
 *
 * @example
 * toCamelCase(undefined);
 * // returns undefined
 *
 * @example
 * toCamelCase('snake_case');
 * // returns 'SnakeCase'
 *
 * @example
 * toCamelCase('customer GPS');
 * // returns 'CustomerGps'
 *
 * @example
 * toCamelCase('IP-address');
 * // returns 'IpAddress'
 *
 * @example
 * toCamelCase('Another & Another, one too');
 * // returns 'AnotherAnotherOneToo'
 *
 * @example
 * toCamelCase('random ----- Thing123');
 * // returns 'RandomThing123'
 *
 * @example
 * toCamelCase('456Thing123');
 * // returns '456Thing123'
 *
 * @example
 * toCamelCase('\n\n');
 * // returns '\n\n'
 *
 * @example
 * toCamelCase(' my Test ');
 * // returns 'MyTest'
 *
 */

export const toCamelCase = string => isBlank(string) ? string : string.match(wordMatcher).map(capitalize).join('');
/**
 * Returns a lower camel case version of a string.
 *
 * @function toLowerCamelCase
 *
 * @param {string} string - string to be converted to lower camel case
 * @return {string} the converted string
 *
 * @example
 * toLowerCamelCase(undefined);
 * // returns undefined
 *
 * @example
 * toLowerCamelCase('snake_case');
 * // returns 'snakeCase'
 *
 * @example
 * toLowerCamelCase('customer GPS');
 * // returns 'customerGps'
 *
 * @example
 * toLowerCamelCase('IP-address');
 * // returns 'ipAddress'
 *
 * @example
 * toLowerCamelCase('Another & Another, one too');
 * // returns 'anotherAnotherOneToo'
 *
 * @example
 * toLowerCamelCase('random ----- Thing123');
 * // returns 'randomThing123'
 *
 * @example
 * toLowerCamelCase('456Thing123');
 * // returns '456Thing123'
 *
 * @example
 * toLowerCamelCase('\n\n');
 * // returns '\n\n'
 *
 * @example
 * toLowerCamelCase(' my Test ');
 * // returns 'myTest'
 *
 */

export const toLowerCamelCase = string => {
  if (isBlank(string)) return string;
  return string.match(wordMatcher).map(capitalize).join('').replace(firstCharMatcher, firstChar => firstChar.toLowerCase());
};
/**
 * Returns a right justified string based on a padding string inspired on Ruby String#rjust method.
 * If size is greater than the length of string, returns a new String of size right justified and padded with padStr; otherwise, returns string.
 *
 * @function rJust
 *
 * @param {string} string - string to be right justified
 * @param {number} size - number of characters to shift
 * @param {string} [padStr= ] - string used to add padding right to string
 * @return {string} the converted string
 *
 * @example
 * rJust('hello', 4);
 * // returns 'hello'
 *
 * @example
 * rJust('hello', 20);
 * // returns '               hello'
 *
 * @example
 * rJust('hello', 20, '1234');
 * // returns '123412341234123hello'
 *
 */

export const rJust = function (string, size) {
  let padStr = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ' ';
  if (string.length >= size) return string;

  const fullSize = () => prefix.length + string.length;

  let prefix = '';

  while (fullSize() < size) prefix += padStr;

  const exceddedSize = fullSize() - size;
  if (exceddedSize > 0) prefix = prefix.substring(0, prefix.length - exceddedSize);
  return prefix + string;
};